import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Grid,
  Icon,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useUsuario } from '../../../providers/usuarioProvider';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import Declaracao from '../../../models/declaracao';
import { getDocs, limit, query, getDoc, onSnapshot } from 'firebase/firestore';
import DeclaracaoDb from '../../../db/declaracaoDb';
import { compact, find, map, orderBy } from 'lodash';
import Posicao from '../../../models/posicao';
import PosicaoDb from '../../../db/posicaoDb';
import moment, { Moment } from 'moment';
import Usuario from '../../../models/usuario';
import UsuarioDb from '../../../db/usuarioDb';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  IconAlertTriangle,
  IconArchive,
  IconCheck,
  IconPlus,
} from '@tabler/icons-react';
import UploadCheckpointDialog from './uploadCheckpointDialog';
import Checkpoint from '../../../models/checkpoint';
import CheckpointDb from '../../../db/checkpointDb';
import TabelaCheckpoint from '../../util/tabelas/tabelaCheckpoint';
import TabelaAtivos from '../../util/tabelas/tabelaAtivos';
import { Link, useNavigate } from 'react-router-dom';
import { radius } from '../../../theme';

type Check = {
  data: Moment;
  tipo: 'Declaração' | 'Posição Inicial' | 'Atualizado' | 'Checkpoint';
};
export default function Checkpoints() {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [declaracoes, setDeclaracoes] = useState<Declaracao[]>([]);
  const [primeiraPosicao, setPrimeiraPosicao] = useState<Posicao>();
  const [usuario, setUsuario] = useState<Usuario>();
  const [posicao, setPosicao] = useState<Posicao>();
  const [checkpoint, setCheckpoint] = useState<Checkpoint | undefined>(
    undefined,
  );
  const [data, setData] = useState<Moment>();
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [openUploadCheckpoint, setOpenUploadCheckpoint] = useState(false);

  useEffect(() => {
    if (!!visualizado) {
      getDocs(
        new DeclaracaoDb().collection({
          usuarioId: visualizado,
          declaracaoId: '',
        }),
      ).then((query) => setDeclaracoes(map(query.docs, (doc) => doc.data())));
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      getDocs(
        query(
          new PosicaoDb().collection({ usuarioId: visualizado, posicaoId: '' }),
          limit(1),
        ),
      ).then((query) =>
        setPrimeiraPosicao(map(query.docs, (doc) => doc.data())[0]),
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      getDoc(new UsuarioDb().doc({ usuarioId: visualizado })).then((doc) =>
        setUsuario(doc.data()),
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      onSnapshot(
        new CheckpointDb().collection({
          usuarioId: visualizado,
          checkpointId: '',
        }),
        (snapshot) => setCheckpoints(map(snapshot.docs, (doc) => doc.data())),
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado && !!data) {
      getDoc(
        new PosicaoDb().doc({
          usuarioId: visualizado,
          posicaoId: data.format('YYYY-MM-DD'),
        }),
      ).then((doc) => setPosicao(doc.data()));
      setCheckpoint(find(checkpoints, (c) => c.data.isSame(data, 'day')));
      navigate(`${location.pathname}#${data!.format('YYYYMMDD')}`, {
        replace: true,
      });
    }
  }, [data]);

  useEffect(() => {
    if (!!location.hash) {
      setData(moment(location.hash.slice(1), 'YYYYMMDD'));
    }
  }, [location.hash]);

  const itens = orderBy(
    compact([
      ...map(declaracoes, (d) => ({
        data: d.dataBase,
        tipo: 'Declaração',
      })),
      !!primeiraPosicao
        ? {
            data: primeiraPosicao.data,
            tipo: 'Posição Inicial',
          }
        : undefined,
      !!usuario
        ? {
            data: usuario.atualizadoAte,
            tipo: 'Atualizado',
          }
        : undefined,
      ...map(checkpoints, (c) => ({
        data: c.data,
        tipo: 'Checkpoint',
      })),
    ]),
    'data',
    'desc',
  ) as Check[];

  const divergencias = (() => {
    if (!posicao?.posicoes || !checkpoint?.posicoes) return 0;
    const posicaoMap = new Map(
      posicao.posicoes.map((p) => [p.codigoAtivo, p.quantidade]),
    );
    const checkpointMap = new Map(
      checkpoint.posicoes.map((c) => [c.codigoAtivo, c.quantidade]),
    );
    const codigos = new Set<string>();
    posicao.posicoes.forEach((p) => codigos.add(p.codigoAtivo));
    checkpoint.posicoes.forEach((c) => codigos.add(c.codigoAtivo));
    let divergenciasCount = 0;
    codigos.forEach((codigo) => {
      const quantidadePosicao = posicaoMap.get(codigo) ?? 0;
      const quantidadeCheckpoint = checkpointMap.get(codigo) ?? 0;
      if (quantidadePosicao !== quantidadeCheckpoint) divergenciasCount += 1;
    });
    return divergenciasCount;
  })();

  return (
    <Container
      maxWidth="lg"
      sx={(theme) => ({
        mb: 2,
        padding: {
          md: theme.spacing(4, 8),
          xs: theme.spacing(2, 2),
        },
      })}
    >
      <Box mb={4}>
        <Typography variant="eyebrow" color="text.secondary">
          Conferência
        </Typography>
        <Typography variant="h2" color="text.primary" gutterBottom>
          Compare qualquer data com a B3
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: "72ch" }}
        >
          Use a timeline à esquerda para navegar entre datas e, quando quiser
          cruzar a posição com a B3, importe um novo ponto de verificação. O
          sistema indica divergências e mostra o que falta cadastrar.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid
          item
          xs="auto"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          order={{ xs: 2, md: 1 }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              sx={{ alignSelf: 'center' }}
              slotProps={{ textField: { size: 'small' } }}
              value={data}
              minDate={primeiraPosicao?.data}
              maxDate={usuario?.atualizadoAte}
              onChange={(e) => {
                if (e?.isValid()) setData(e.startOf('day'));
              }}
            />
          </LocalizationProvider>
          <Box
            sx={{
              maxHeight: 520,
              overflowY: 'auto',
              mt: 2,
              pr: 1,
            }}
          >
            <Timeline position="right" sx={{ alignItems: 'flex-start' }}>
              {map(itens, (i) => {
                // Cada estado do fluxo cai em UMA das cinco cores semânticas
                // de chip de status — nunca uma sexta.
                let tint: 'positive' | 'accent' | 'neutral' = 'neutral';
                switch (i.tipo) {
                  case 'Atualizado':
                    tint = 'positive';
                    break;
                  case 'Checkpoint':
                    tint = 'accent';
                    break;
                  case 'Declaração':
                  case 'Posição Inicial':
                    tint = 'neutral';
                    break;
                }
                const isSelected = data?.isSame(i.data, 'day');

                return (
                  <TimelineItem key={i.data.unix()}>
                    <TimelineOppositeContent>
                      {/* Data em mono tabular: as datas alinham em coluna. */}
                      <Typography
                        variant="dataMono"
                        width={72}
                        display="block"
                        textAlign="right"
                        color="text.secondary"
                        sx={{ fontSize: 12 }}
                      >
                        {i.data.format('DD/MM/YYYY')}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <Box
                        component="span"
                        role="button"
                        aria-pressed={isSelected}
                        tabIndex={0}
                        onClick={() => setData(i.data)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setData(i.data);
                          }
                        }}
                        sx={(theme) => ({
                          width: 14,
                          height: 14,
                          marginY: 1,
                          borderRadius: radius.round,
                          border: '2px solid',
                          // Tinta do accent: o dot precisa de 3:1 nos dois temas.
                          borderColor: isSelected
                            ? theme.palette.accentInk
                            : theme.palette.divider,
                          backgroundColor: isSelected
                            ? theme.palette.accentInk
                            : 'transparent',
                          cursor: 'pointer',
                          transition:
                            'border-color 120ms ease-out, background-color 120ms ease-out',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': {
                            borderColor: isSelected
                              ? theme.palette.accentInk
                              : theme.palette.ink[40],
                          },
                        })}
                      />

                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Chip
                        size="small"
                        label={i.tipo}
                        sx={(theme) => ({
                          bgcolor: theme.palette.statusChip[tint].bg,
                          color: theme.palette.statusChip[tint].fg,
                        })}
                      />
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </Box>
        </Grid>
        <Grid item xs order={{ xs: 1, md: 2 }}>
          <Card
            elevation={0}
            // Painel grande: radius-xl. A hierarquia vem da hairline do tema.
            sx={{ width: '100%', borderRadius: `${radius.xl}px` }}
          >
            <CardHeader
              title={
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h4" color="text.primary">
                    Posição em
                  </Typography>
                  {!!data ? (
                    <Typography
                      variant="dataMono"
                      color="text.primary"
                      sx={{ fontSize: 16, fontWeight: 500 }}
                    >
                      {data.format('DD/MM/YYYY')}
                    </Typography>
                  ) : (
                    <Skeleton
                      variant="text"
                      animation={false}
                      width={80}
                      sx={{ display: 'inline-block' }}
                    />
                  )}
                </Box>
              }
              action={
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<IconPlus />}
                    onClick={() => setOpenUploadCheckpoint(true)}
                  >
                    Novo Ponto de Verificação
                  </Button>
                  <UploadCheckpointDialog
                    open={openUploadCheckpoint}
                    onClose={() => setOpenUploadCheckpoint(false)}
                  />
                </>
              }
            />
            <CardContent>
              {checkpoint && (
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={1}
                  alignItems="center"
                  mb={2}
                >
                  {/* Resultado da conferência é status: tint semântico + hairline. */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={(theme) => ({
                      px: 1.5,
                      py: 1,
                      borderRadius: `${radius.lg}px`,
                      bgcolor:
                        theme.palette.statusChip[
                          divergencias > 0 ? 'warn' : 'positive'
                        ].bg,
                      color:
                        theme.palette.statusChip[
                          divergencias > 0 ? 'warn' : 'positive'
                        ].fg,
                    })}
                  >
                    {divergencias > 0 ? (
                      <Icon color="inherit" fontSize="small">
                        <IconAlertTriangle size="inherit" />
                      </Icon>
                    ) : (
                      <Icon color="inherit" fontSize="small">
                        <IconCheck size="inherit" />
                      </Icon>
                    )}
                    <Typography variant="body2" color="inherit">
                      <Typography
                        component="span"
                        variant="dataMono"
                        sx={{ fontWeight: 600 }}
                      >
                        {divergencias}
                      </Typography>{' '}
                      divergência{divergencias === 1 ? '' : 's'} encontradas.
                      Faltou cadastrar alguma nota?
                    </Typography>
                  </Box>
                  <Button
                    component={Link}
                    to="/arquivo"
                    variant="outlined"
                    startIcon={<IconArchive />}
                  >
                    Sim, cadastrar nota
                  </Button>
                </Box>
              )}
              {data ? (
                checkpoint ? (
                  <>
                    <TabelaCheckpoint
                      ativos={posicao?.posicoes}
                      checkpoints={checkpoint.posicoes}
                      loading={!!!data || !data.isSame(posicao?.data, 'day')}
                    />
                  </>
                ) : (
                  <>
                    <TabelaAtivos
                      ativos={posicao?.posicoes}
                      loading={!!!data || !data.isSame(posicao?.data, 'day')}
                    />
                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      mt={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Quer criar um ponto de verificação nessa data? Importe o
                        extrato para cruzar os dados.
                      </Typography>
                      <Button
                        variant="text"
                        // Botão de texto usa a TINTA do accent, não o accent sólido.
                        sx={(theme) => ({ color: theme.palette.accentInk })}
                        onClick={() => setOpenUploadCheckpoint(true)}
                      >
                        Importar extrato B3
                      </Button>
                    </Box>
                  </>
                )
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  alignItems="flex-start"
                >
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Selecione uma data na timeline para ver a posição nesse dia.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<IconPlus />}
                    onClick={() => setOpenUploadCheckpoint(true)}
                  >
                    Importar extrato da B3
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
